// Angular
import {Injectable} from '@angular/core';
// RxJS
import {of, forkJoin} from 'rxjs';
import {mergeMap, map, withLatestFrom, filter, tap} from 'rxjs/operators';
// NGRX
import {Effect, Actions, ofType} from '@ngrx/effects';
import {Store, select} from '@ngrx/store';
// CRUD
import {QueryParamsModel} from '../../_base/crud';
// Services
import {LocationDescriptionsService} from '../_services/location-description.service';
// State
import {AppState} from '../../reducers';
// Selectors
import {allLocationDescriptionsLoaded} from '../_selectors/location-description.selectors';
// Actions
import {
    AllLocationDescriptionsLoaded,
    AllLocationDescriptionsRequested,
    LocationDescriptionActionTypes,
    LocationDescriptionsPageRequested,
    LocationDescriptionsPageLoaded,
    LocationDescriptionUpdated,
    LocationDescriptionsPageToggleLoading,
    LocationDescriptionDeleted,
    LocationDescriptionOnServerCreated,
    LocationDescriptionCreated,
    LocationDescriptionAdminRestored,
    LocationDescriptionDeletedFromAdminTrash,
    LocationDescriptionDeletedFromTrash,
    LocationDescriptionOnServerAdminRestored,
    LocationDescriptionOnServerRestored,
    LocationDescriptionRestored,
    LocationDescriptionTrashFlushed,
    LocationDescriptionsActionToggleLoading
} from '../_actions/location-description.actions';

@Injectable()
export class LocationDescriptionEffects {
    showPageLoadingDispatcher = new LocationDescriptionsPageToggleLoading({isLoading: true});
    showActionLoadingDispatcher = new LocationDescriptionsActionToggleLoading({isLoading: true});
    hideActionLoadingDispatcher = new LocationDescriptionsActionToggleLoading({isLoading: false});

    @Effect()
    loadAllLocationDescriptions$ = this.actions$
        .pipe(
            ofType<AllLocationDescriptionsRequested>(LocationDescriptionActionTypes.AllLocationDescriptionsRequested),
            withLatestFrom(this.store.pipe(select(allLocationDescriptionsLoaded))),
            filter(([action, isAllLocationDescriptionsLoaded]) => !isAllLocationDescriptionsLoaded),
            mergeMap(() => this.service.getAllLocationDescriptions()),
            map(res => {
                return new AllLocationDescriptionsLoaded({locationDescriptions: res.data});
            })
        );

    @Effect()
    loadLocationDescriptionsPage$ = this.actions$
        .pipe(
            ofType<LocationDescriptionsPageRequested>(LocationDescriptionActionTypes.LocationDescriptionsPageRequested),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showPageLoadingDispatcher);
                const requestToServer = this.service.findLocationDescriptions(payload.page);
                const lastQuery = of(payload.page);
                return forkJoin(requestToServer, lastQuery);
            }),
            map(response => {
                const result: any = response[0];
                const lastQuery: QueryParamsModel = response[1];
                return new LocationDescriptionsPageLoaded({
                    locationDescriptions: result.data,
                    totalCount: result.pagination.total,
                    totalTrashed: result.pagination.total_trashed,
                    totalAdminTrashed: result.pagination.admin_trashed,
                    page: lastQuery
                });
            }),
        );

    @Effect()
    deleteLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionDeleted>(LocationDescriptionActionTypes.LocationDescriptionDeleted),
            mergeMap(({payload}) => {
                    this.store.dispatch(this.showActionLoadingDispatcher);
                    return this.service.deleteLocationDescription(payload.id);
                }
            ),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    updateLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionUpdated>(LocationDescriptionActionTypes.LocationDescriptionUpdated),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.updateLocationDescription(payload.locationDescription);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );


    @Effect()
    createLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionOnServerCreated>(LocationDescriptionActionTypes.LocationDescriptionOnServerCreated),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.createLocationDescription(payload.locationDescription).pipe(
                    tap(res => {
                        this.store.dispatch(new LocationDescriptionCreated({locationDescription: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    flushLocationDescriptionTrash$ = this.actions$
        .pipe(
            ofType<LocationDescriptionTrashFlushed>(LocationDescriptionActionTypes.LocationDescriptionTrashFlushed),
            mergeMap(() => {
                    return this.service.flushTrash();
                }
            ),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    restoreLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionOnServerRestored>(LocationDescriptionActionTypes.LocationDescriptionOnServerRestored),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.restoreFromTrash(payload.id).pipe(
                    tap(res => {
                        this.store.dispatch(new LocationDescriptionRestored({item: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    restoreAdminLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionOnServerAdminRestored>(LocationDescriptionActionTypes.LocationDescriptionOnServerAdminRestored),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.restoreFromTrash(payload.id).pipe(
                    tap(res => {
                        this.store.dispatch(new LocationDescriptionAdminRestored({item: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    deleteFromTrashLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionDeletedFromTrash>(LocationDescriptionActionTypes.LocationDescriptionDeletedFromTrash),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.deleteFromTrash(payload.id);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    deleteFromAdminTrashLocationDescription$ = this.actions$
        .pipe(
            ofType<LocationDescriptionDeletedFromAdminTrash>(LocationDescriptionActionTypes.LocationDescriptionDeletedFromAdminTrash),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.deleteFromAdminTrash(payload.id);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    constructor(private actions$: Actions, private service: LocationDescriptionsService, private store: Store<AppState>) {
    }
}
